import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Rss, Send, RefreshCw, X, Repeat2, MessageCircle, Share2,
  Bookmark, BarChart2, Image as ImageIcon
} from "lucide-react";
import { BACKEND_URL, ROLE_COLORS, POST_TYPE_CFG } from "./networkConstants";
import { relativeTime } from "./networkUtils";
import { AuthorAvatar, LiveTicker } from "./NetworkShared";
import {
  QuickActionsPanel, DealInsightsWidget, HotDealsWidget,
  CapitalReadyWidget, LeaderboardWidget, LiveActivityWidget,
  WatchlistWidget, DealScoreLeaderboardWidget
} from "./NetworkWidgets";
import { DealAlertWidget } from "./DealAlertWidget";

// ─── Reaction config ───────────────────────────────────────────────────────────
const REACTIONS_CFG = [
  { key: "hot",        emoji: "🔥", label: "Hot",        active: "text-orange-400 bg-orange-500/10"   },
  { key: "money",      emoji: "💰", label: "Money",      active: "text-[#D4AF37] bg-[#D4AF37]/10"    },
  { key: "watching",   emoji: "👀", label: "Watching",   active: "text-blue-400 bg-blue-500/10"       },
  { key: "interested", emoji: "🤝", label: "Interested", active: "text-emerald-400 bg-emerald-500/10" },
];

// ─── Quote Modal ───────────────────────────────────────────────────────────────
function QuoteModal({ post, user, onClose, onPosted }) {
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const authorName = post.author?.display_name || post.author?.name || "Network Member";

  const submit = async () => {
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/network/posts/${post.post_id}/quote`,
        { commentary: text },
        { withCredentials: true }
      );
      toast.success("Reposted to your network!");
      onPosted(res.data);
      onClose();
    } catch { toast.error("Failed to repost"); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#0D0D0D] border border-white/[0.1] rounded-2xl w-full max-w-lg shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-white/[0.06]">
          <p className="text-white font-semibold text-sm flex items-center gap-2">
            <Repeat2 className="w-4 h-4 text-[#D4AF37]" /> Quote Post
          </p>
          <button onClick={onClose} className="p-1 text-white/30 hover:text-white"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-4 space-y-3">
          <div className="flex gap-3">
            <AuthorAvatar picture={user?.picture} name={user?.name || user?.email} role={user?.user_type} />
            <textarea value={text} onChange={e => setText(e.target.value)} placeholder="Add your take…" rows={3}
              className="flex-1 bg-transparent text-white/80 text-sm resize-none focus:outline-none placeholder:text-white/20" />
          </div>
          <div className="border border-white/[0.08] rounded-xl p-3 bg-white/[0.02]">
            <div className="flex items-center gap-2 mb-1.5">
              <AuthorAvatar picture={post.author?.profile_picture_url} name={authorName} role={post.author?.user_type} size="sm" />
              <span className="text-white/50 text-xs font-medium">{authorName}</span>
              <span className="text-white/20 text-[10px]">{relativeTime(post.created_at)}</span>
            </div>
            <p className="text-white/40 text-xs line-clamp-3">{post.content}</p>
          </div>
          <button onClick={submit} disabled={submitting}
            className="w-full py-2.5 bg-gradient-to-r from-[#D4AF37] to-[#C4A030] text-black font-bold text-sm rounded-xl hover:from-[#C4A030] hover:to-[#B49020] disabled:opacity-40 transition-all">
            {submitting ? "Reposting…" : "Repost Now"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Post Card ─────────────────────────────────────────────────────────────────
function PostCard({ post, user, onQuote, onRefresh }) {
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [submittingComment, setSubmittingComment] = useState(false);
  const [reactionCounts, setReactionCounts] = useState(post.reaction_counts || {});
  const [userReaction, setUserReaction] = useState(post.user_reaction || null);
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef(null);

  const isOwn     = post.author_id === user?.user_id || post.user_id === user?.user_id;
  const authorName = post.author?.display_name || post.author?.name || "Network Member";
  const authorPic  = post.author?.profile_picture_url;
  const authorRole = post.author?.user_type || post.author?.primary_role;
  const roleCfg    = ROLE_COLORS[authorRole] || ROLE_COLORS.default;
  const ptCfg      = POST_TYPE_CFG[post.post_type] || POST_TYPE_CFG.update;
  const liked      = post.user_liked;
  const isHot      = (post.like_count || 0) + Object.values(reactionCounts).reduce((s, v) => s + v, 0) > 3;
  const totalReactions = (post.like_count || 0) + Object.values(reactionCounts).reduce((s, v) => s + v, 0);

  useEffect(() => {
    if (!showMenu) return;
    const handler = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setShowMenu(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showMenu]);

  const react = async (reactionType) => {
    const res = await axios.post(
      `${BACKEND_URL}/api/network/posts/${post.post_id}/react`,
      { reaction_type: reactionType }, { withCredentials: true }
    ).catch(() => null);
    if (res) { setReactionCounts(res.data.reaction_counts || {}); setUserReaction(res.data.user_reaction); }
  };

  const deletePost = async () => {
    setShowMenu(false);
    if (!window.confirm("Delete this post?")) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/network/posts/${post.post_id}`, { withCredentials: true });
      toast.success("Post deleted");
      onRefresh?.();
    } catch { toast.error("Failed to delete post"); }
  };

  const copyText = () => {
    navigator.clipboard.writeText(post.content || "");
    toast.success("Copied to clipboard");
    setShowMenu(false);
  };

  const toggleComments = async () => {
    if (!showComments && !comments.length) {
      const res = await axios.get(`${BACKEND_URL}/api/network/posts/${post.post_id}/comments`, { withCredentials: true }).catch(() => null);
      if (res) setComments(res.data?.comments || []);
    }
    setShowComments(s => !s);
  };

  const addComment = async () => {
    if (!newComment.trim()) return;
    setSubmittingComment(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/network/posts/${post.post_id}/comments`,
        { content: newComment }, { withCredentials: true });
      setComments(c => [...c, res.data]);
      setNewComment("");
    } catch { toast.error("Failed to comment"); }
    finally { setSubmittingComment(false); }
  };

  return (
    <div data-testid={`post-${post.post_id}`}
      className={`bg-[#0D0D0D] border rounded-2xl overflow-hidden transition-all group ${
        isHot ? 'border-orange-500/20 shadow-[0_0_20px_rgba(251,146,60,0.05)] hover:border-orange-500/30' : 'border-white/[0.06] hover:border-white/[0.1]'
      }`}>

      {isHot && <div className="h-0.5 bg-gradient-to-r from-orange-500/50 via-[#D4AF37]/50 to-transparent" />}

      <div className="flex items-start gap-3 p-4 pb-0">
        <AuthorAvatar picture={authorPic} name={authorName} role={authorRole} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-white font-semibold text-sm">{authorName}</span>
            {post.author?.organization_name && <span className="text-white/30 text-[11px]">· {post.author.organization_name}</span>}
            {isHot && <span className="text-[9px] text-orange-400 bg-orange-500/10 border border-orange-500/20 px-1.5 py-0.5 rounded-full font-bold">HOT</span>}
          </div>
          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${roleCfg.bg} ${roleCfg.color} font-medium border ${roleCfg.border}`}>
              {roleCfg.label}
            </span>
            {ptCfg && (
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${ptCfg.bg} ${ptCfg.color} border ${ptCfg.border} font-medium`}>
                {ptCfg.emoji} {ptCfg.label}
              </span>
            )}
            <span className="text-white/20 text-[10px]">{relativeTime(post.created_at)}</span>
          </div>
        </div>
        <div className="relative" ref={menuRef}>
          <button onClick={() => setShowMenu(s => !s)} data-testid={`post-menu-${post.post_id}`}
            className={`p-1.5 rounded-lg transition-colors ${showMenu ? 'text-white/70 bg-white/[0.06]' : 'text-white/20 hover:text-white/50 opacity-0 group-hover:opacity-100'}`}>
            <span className="text-white/40">···</span>
          </button>
          {showMenu && (
            <div className="absolute right-0 top-full mt-1 w-44 bg-[#181818] border border-white/[0.1] rounded-xl shadow-2xl shadow-black/60 overflow-hidden z-50 animate-fade-up">
              <button onClick={copyText}
                className="w-full flex items-center gap-2.5 px-3 py-2.5 text-xs text-white/60 hover:text-white hover:bg-white/[0.06] transition-all text-left">
                <Share2 className="w-3.5 h-3.5" /> Copy text
              </button>
              {isOwn ? (
                <button onClick={deletePost} data-testid={`delete-post-${post.post_id}`}
                  className="w-full flex items-center gap-2.5 px-3 py-2.5 text-xs text-red-400 hover:text-red-300 hover:bg-red-500/[0.08] transition-all text-left border-t border-white/[0.06]">
                  <X className="w-3.5 h-3.5" /> Delete post
                </button>
              ) : (
                <button onClick={() => { setShowMenu(false); toast.info("Reported — we'll review this post."); }}
                  className="w-full flex items-center gap-2.5 px-3 py-2.5 text-xs text-white/40 hover:text-white/70 hover:bg-white/[0.04] transition-all text-left border-t border-white/[0.06]">
                  Report post
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="px-4 pt-3 pb-2">
        <p className="text-white/80 text-sm leading-relaxed whitespace-pre-line">{post.content}</p>
      </div>

      {post.images?.length > 0 && (
        <div className="px-4 pb-3 space-y-2">
          {post.images.map((img, i) => {
            const src = img.data
              ? (img.data.startsWith("data:") ? img.data : `data:${img.content_type || "image/jpeg"};base64,${img.data}`)
              : img.url || img;
            if (!src) return null;
            return (
              <img key={i} src={src} alt="" onError={e => { e.target.style.display = "none"; }}
                className="w-full rounded-xl object-cover max-h-80 border border-white/[0.06]" />
            );
          })}
        </div>
      )}

      {totalReactions > 0 && (
        <div className="px-4 pb-1 flex items-center gap-1.5">
          {REACTIONS_CFG.filter(r => reactionCounts[r.key] > 0).map(r => (
            <span key={r.key} className="text-xs text-white/30">{r.emoji}</span>
          ))}
          {liked && <span className="text-xs text-white/30">❤️</span>}
          <span className="text-[10px] text-white/20 ml-1">{totalReactions} reaction{totalReactions !== 1 ? 's' : ''}</span>
        </div>
      )}

      <div className="flex items-center px-2 py-2 border-t border-white/[0.04]">
        {REACTIONS_CFG.map(r => (
          <button key={r.key} onClick={() => react(r.key)} title={r.label}
            className={`flex items-center gap-1 px-2 py-1.5 rounded-xl transition-all text-xs ${
              userReaction === r.key ? r.active : 'text-white/25 hover:text-white/60 hover:bg-white/[0.04]'
            }`}>
            <span className="text-sm leading-none">{r.emoji}</span>
            {reactionCounts[r.key] > 0 && <span className="text-[10px] font-semibold">{reactionCounts[r.key]}</span>}
          </button>
        ))}
        <button onClick={toggleComments}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl transition-all text-xs ml-1 ${
            showComments ? 'text-blue-400 bg-blue-500/10' : 'text-white/25 hover:text-blue-400 hover:bg-blue-500/[0.07]'
          }`}>
          <MessageCircle className="w-3.5 h-3.5" />
          <span>{post.comment_count || 0}</span>
        </button>
        <button onClick={() => onQuote(post)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-white/25 hover:text-emerald-400 hover:bg-emerald-500/[0.07] transition-all text-xs">
          <Repeat2 className="w-3.5 h-3.5" />
        </button>
        <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-white/25 hover:text-[#D4AF37] hover:bg-[#D4AF37]/[0.07] transition-all text-xs ml-auto">
          <Bookmark className="w-3.5 h-3.5" />
        </button>
      </div>

      {showComments && (
        <div className="border-t border-white/[0.04] bg-black/20 px-4 py-3 space-y-3">
          {comments.map(c => {
            const cAuthor = c.author?.display_name || c.author?.name || "Member";
            return (
              <div key={c.comment_id} className="flex gap-2.5 animate-fade-up">
                <AuthorAvatar picture={c.author?.profile_picture_url} name={cAuthor} role={c.author?.user_type} size="sm" />
                <div className="flex-1 bg-white/[0.03] border border-white/[0.05] rounded-xl px-3 py-2">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-white/70 text-xs font-semibold">{cAuthor}</span>
                    <span className="text-white/20 text-[10px]">{relativeTime(c.created_at)}</span>
                  </div>
                  <p className="text-white/60 text-xs leading-snug">{c.content}</p>
                </div>
              </div>
            );
          })}
          <div className="flex gap-2.5 mt-2">
            <AuthorAvatar picture={user?.profile_picture_url || user?.picture} name={user?.name || "You"} role={user?.user_type} size="sm" />
            <div className="flex-1 flex items-center gap-2">
              <input value={newComment} onChange={e => setNewComment(e.target.value)}
                onKeyDown={e => e.key === "Enter" && !e.shiftKey && addComment()}
                placeholder="Add a comment…"
                className="flex-1 bg-white/[0.04] border border-white/[0.08] rounded-xl px-3 py-2 text-xs text-white placeholder:text-white/20 focus:outline-none focus:border-[#D4AF37]/30" />
              <button onClick={addComment} disabled={submittingComment || !newComment.trim()}
                className="p-2 bg-[#D4AF37] hover:bg-[#C4A030] disabled:opacity-30 text-black rounded-lg transition-all">
                <Send className="w-3 h-3" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Feed Tab ──────────────────────────────────────────────────────────────────
const POST_TYPES = [
  { key: "update",          emoji: "💬", label: "Update"    },
  { key: "deal_alert",      emoji: "🟢", label: "Deal Drop" },
  { key: "funding_update",  emoji: "💰", label: "Funding"   },
  { key: "win",             emoji: "🏆", label: "Win"       },
  { key: "partner_request", emoji: "🤝", label: "Partner"   },
  { key: "insight",         emoji: "🧠", label: "Insight"   },
];

export function FeedTab({ user, onDealClick }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newPost, setNewPost] = useState("");
  const [postType, setPostType] = useState("update");
  const [posting, setPosting] = useState(false);
  const [quotePost, setQuotePost] = useState(null);
  const [tickerItems, setTickerItems] = useState([]);
  const [myProfile, setMyProfile] = useState(null);
  const [postImageFile, setPostImageFile] = useState(null);
  const [postImagePreview, setPostImagePreview] = useState(null);
  const [showMobileWidgets, setShowMobileWidgets] = useState(false);
  const imageInputRef = useRef(null);

  useEffect(() => {
    fetchPosts();
    axios.get(`${BACKEND_URL}/api/network/activity`, { withCredentials: true })
      .then(r => setTickerItems(r.data || [])).catch(() => {});
    axios.get(`${BACKEND_URL}/api/profile`, { withCredentials: true })
      .then(r => setMyProfile(r.data)).catch(() => {});
  }, []);

  const fetchPosts = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/network/posts`, { withCredentials: true });
      setPosts(res.data?.posts || res.data || []);
    } catch { }
    finally { setLoading(false); }
  };

  const submitPost = async () => {
    if (!newPost.trim() && !postImageFile) return;
    setPosting(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/network/posts`,
        { content: newPost, post_type: postType, visibility: "public" }, { withCredentials: true });
      const newPostData = res.data?.post || res.data;
      if (postImageFile && newPostData.post_id) {
        try {
          const fd = new FormData();
          fd.append("file", postImageFile);
          await axios.post(`${BACKEND_URL}/api/network/posts/${newPostData.post_id}/image`, fd, {
            withCredentials: true, headers: { "Content-Type": "multipart/form-data" }
          });
          newPostData.images = [{ data: postImagePreview, content_type: postImageFile.type }];
        } catch { /* non-fatal */ }
      }
      setPosts(p => [newPostData, ...p]);
      setNewPost("");
      setPostImageFile(null);
      setPostImagePreview(null);
      toast.success("Posted to your network!");
    } catch { toast.error("Failed to post"); }
    finally { setPosting(false); }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Image must be under 5MB"); return; }
    setPostImageFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setPostImagePreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const myPicture = myProfile?.profile_picture_url || user?.picture || user?.profile_picture_url;
  const myName    = myProfile?.display_name || user?.name || user?.email || "You";

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <RefreshCw className="w-5 h-5 text-white/20 animate-spin" />
    </div>
  );

  return (
    <div className="py-4 px-3 sm:px-5">
      <LiveTicker items={tickerItems} />

      {/* Mobile-only quick stats strip */}
      <div className="flex lg:hidden items-center gap-2 mt-3 overflow-x-auto scrollbar-none pb-1">
        <div className="flex items-center gap-2 px-3 py-2 bg-[#0D0D0D] border border-white/[0.06] rounded-xl text-xs flex-shrink-0">
          <span className="w-1.5 h-1.5 rounded-full bg-[#00FF88] animate-pulse" />
          <span className="text-white/50 font-medium">Live Network</span>
        </div>
        <button onClick={() => setShowMobileWidgets(s => !s)}
          className="flex items-center gap-1.5 px-3 py-2 bg-[#0D0D0D] border border-[#D4AF37]/20 rounded-xl text-xs text-[#D4AF37] font-medium flex-shrink-0">
          Deal Score Board
        </button>
        <button onClick={() => setShowMobileWidgets(s => !s)}
          className="flex items-center gap-1.5 px-3 py-2 bg-[#0D0D0D] border border-white/[0.06] rounded-xl text-xs text-white/40 font-medium flex-shrink-0">
          Capital Ready
        </button>
        <button onClick={() => setShowMobileWidgets(s => !s)}
          className="flex items-center gap-1.5 px-3 py-2 bg-[#0D0D0D] border border-white/[0.06] rounded-xl text-xs text-white/40 font-medium flex-shrink-0">
          Insights
        </button>
      </div>

      {/* Mobile widgets drawer */}
      {showMobileWidgets && (
        <div className="lg:hidden mt-3 space-y-3">
          <DealAlertWidget />
          <DealScoreLeaderboardWidget onDealClick={onDealClick} />
          <DealInsightsWidget />
          <CapitalReadyWidget />
        </div>
      )}

      <div className="mt-4 flex gap-5 max-w-7xl mx-auto">
        {/* LEFT column */}
        <div className="hidden xl:flex flex-col gap-4 w-52 flex-shrink-0">
          <QuickActionsPanel onStartPost={(type) => setPostType(type)} />
          <DealAlertWidget />
          <WatchlistWidget onDealClick={onDealClick} />
        </div>

        {/* CENTER column */}
        <div className="flex-1 min-w-0 space-y-3">
          {/* Compose card */}
          <div className="bg-[#0D0D0D] border border-white/[0.07] rounded-2xl overflow-hidden">
            <div className="flex border-b border-white/[0.04] overflow-x-auto scrollbar-none">
              {POST_TYPES.map(pt => (
                <button key={pt.key} onClick={() => setPostType(pt.key)}
                  className={`flex items-center gap-1.5 px-3 py-2.5 text-[10px] font-bold tracking-wide transition-all whitespace-nowrap flex-shrink-0 ${
                    postType === pt.key ? "text-white border-b-2 border-[#D4AF37]" : "text-white/25 hover:text-white/50"
                  }`}>
                  <span>{pt.emoji}</span>{pt.label}
                </button>
              ))}
            </div>
            <div className="flex gap-3 p-4">
              <AuthorAvatar picture={myPicture} name={myName} role={user?.user_type} />
              <div className="flex-1">
                <textarea value={newPost} onChange={e => setNewPost(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) submitPost(); }}
                  placeholder={
                    postType === "deal_alert"       ? "Drop a deal — address, ARV, asking price…"  :
                    postType === "win"              ? "Tell the network about your win…"           :
                    postType === "partner_request"  ? "What are you looking for? JV, lender, buyer…" :
                    postType === "funding_update"   ? "Capital committed, raise update, funding news…" :
                    "What's moving in your market?"
                  }
                  rows={3} data-testid="compose-post-input"
                  className="w-full bg-transparent text-white/80 text-sm resize-none focus:outline-none placeholder:text-white/20 leading-relaxed" />

                {postImagePreview && (
                  <div className="relative mt-2 mb-2 rounded-xl overflow-hidden border border-white/[0.08]">
                    <img src={postImagePreview} alt="Preview" className="w-full max-h-48 object-cover" />
                    <button onClick={() => { setPostImageFile(null); setPostImagePreview(null); }}
                      className="absolute top-2 right-2 w-6 h-6 bg-black/70 rounded-full flex items-center justify-center text-white/70 hover:text-white transition-all">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                )}

                <div className="flex items-center justify-between mt-2 pt-2 border-t border-white/[0.05]">
                  <div className="flex items-center gap-1">
                    <input ref={imageInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={handleImageSelect} />
                    <button onClick={() => imageInputRef.current?.click()}
                      className={`p-1.5 rounded-lg transition-all ${postImageFile ? 'text-[#D4AF37] bg-[#D4AF37]/10' : 'text-white/20 hover:text-[#D4AF37]'}`}
                      data-testid="attach-image-btn" title="Attach photo">
                      <ImageIcon className="w-4 h-4" />
                    </button>
                    <button className="p-1.5 text-white/20 hover:text-blue-400 rounded-lg transition-all">
                      <BarChart2 className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="flex items-center gap-3">
                    {newPost.length > 0 && (
                      <span className={`text-[10px] ${newPost.length > 1800 ? 'text-red-400' : 'text-white/20'}`}>{2000 - newPost.length}</span>
                    )}
                    <button onClick={submitPost} disabled={posting || (!newPost.trim() && !postImageFile)}
                      data-testid="submit-post-btn"
                      className="px-5 py-1.5 bg-gradient-to-r from-[#D4AF37] to-[#C4A030] hover:from-[#C4A030] hover:to-[#B49020] disabled:opacity-30 text-black font-bold text-xs rounded-full transition-all">
                      {posting ? "Posting…" : "Post"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {posts.length > 0 && (
            <div className="flex items-center gap-3">
              <div className="flex-1 h-px bg-white/[0.04]" />
              <span className="text-[9px] text-white/20 uppercase tracking-widest">Recent Activity</span>
              <div className="flex-1 h-px bg-white/[0.04]" />
            </div>
          )}

          {posts.length === 0 ? (
            <div className="text-center py-16 bg-[#0A0A0A] border border-dashed border-white/[0.06] rounded-2xl">
              <div className="w-14 h-14 rounded-2xl bg-[#D4AF37]/[0.05] border border-[#D4AF37]/10 flex items-center justify-center mx-auto mb-3">
                <Rss className="w-6 h-6 text-[#D4AF37]/30" />
              </div>
              <p className="text-white/40 text-sm font-medium">The battlefield is quiet…</p>
              <p className="text-white/20 text-xs mt-1">Drop a deal, share a win, or post market intel</p>
            </div>
          ) : (
            <div className="space-y-2">
              {posts.map(post => (
                <PostCard key={post.post_id || post._id} post={post} user={user}
                  onQuote={setQuotePost} onRefresh={fetchPosts} />
              ))}
            </div>
          )}
        </div>

        {/* RIGHT column */}
        <div className="hidden lg:flex flex-col gap-4 w-72 flex-shrink-0">
          <DealScoreLeaderboardWidget onDealClick={onDealClick} />
          <DealInsightsWidget />
          <HotDealsWidget onDealClick={onDealClick} />
          <CapitalReadyWidget />
          <LeaderboardWidget />
          <LiveActivityWidget />
        </div>
      </div>

      {quotePost && (
        <QuoteModal post={quotePost} user={user}
          onClose={() => setQuotePost(null)}
          onPosted={np => setPosts(p => [np, ...p])} />
      )}
    </div>
  );
}
